// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import LazyLoad from "react-lazyload";

// Import Material UI
import Dialog from '@material-ui/core/Dialog';
import { Close } from '@material-ui/icons';
import { Button } from "@material-ui/core";

// Import Pages
import { alertState } from '../../../../../recoil-store/recoil-state';
import StepBar from '../../../../../components/StepBar/StepBar';

// Import Helpers
import { sleep } from '../../../../../helpers/helper';

// Import Services
import LibraryService from '../../../../../services/LibraryService/LibraryService';

// Import Icons
import successIcon from '../../../../../assets/icons/success-icon.svg';
import warningIcon from '../../../../../assets/icons/red-warning-icon.svg';
import upArrowIcon from '../../../../../assets/icons/up-arrow-icon.svg';
import downArrowIcon from '../../../../../assets/icons/down-arrow-icon.svg';

// Import CSS
import './RequiredLibraries.css';

function RequiredLibraries({ data, handleClose, downloadBot, differentLibraryVersions, outLabel, callFunction, handleCloseOutPanel }) {
    const label = outLabel ? outLabel : 'CONTENT.BOT-DETAILS.REQUIRED-LIBRARY.';
    const { t } = useTranslation();
    const [libraries, setLibraries] = useState();
    const [downloadLibraryError, setDownloadLibraryError] = useState();
    const [collapse, setCollapse] = useState(false);
    const [information, setInformation] = useState({ percentage: '10%', message: t(label + 'CHECKING-LIBRARIES') }); // type => info,error,warning, visible:true,message:''
    const [customColumn, setCustomColumn] = useState([]);
    const setAlert = useSetRecoilState(alertState);
    const messageLabel = 'MESSAGES.STORE.';

    useEffect(() => {
        async function checkRequiredLibraries() {
            setLibraries(data.required_libraries);
            /* checking libraries */
            const result = await LibraryService.checkNonExistingLibraries({ required_libraries: data.required_libraries });
            if (!result.status) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(messageLabel + result.message),
                    severity: 'error'
                }))
                setInformation(undefined);
                handleClose();
            } else {
                /* detect libraries */
                if (result.data && result.data.length) {
                    setTimeout(() => {
                        setLibraries(result.data);
                        /* checking system libraries */
                        if (result.data.find(x => x.is_system)) {
                            handleClose();
                            setInformation(undefined);
                            setAlert(alert => ({
                                ...alert,
                                show: true,
                                message: t(label + 'MINIMUM-PRODUCT-VERSION') + data?.product_version,
                                severity: 'warning'
                            }))
                        } else {
                            /* incompatible libraries found. Starting the listing process */
                            setInformation(undefined);
                        }
                    }, 5000)

                } else if (downloadBot) {
                    await sleep(3000)
                    /* incompatible library not found. The bot is downloading */
                    setInformation({ percentage: '70%', message: t(label + 'DOWNLOADING-AUTOM') })
                    downloadBot((response) => {
                        if (response.status) {
                            setInformation({ percentage: undefined, icon: successIcon, message: t(label + 'DOWNLOADED-SUCCESSFUL') })
                        }
                        setTimeout(() => {
                            handleClose();
                            setInformation(undefined)
                        }, 3000)
                    });
                }
                else {
                    setTimeout(() => {
                        if (callFunction) {
                            callFunction()
                        }
                        setInformation({ percentage: undefined, icon: successIcon, message: t(label + 'VERSİONS-SUCCESSFUL') })
                        handleClose();
                        setInformation(undefined)
                    }, 3000)
                }
            }
        }
        checkRequiredLibraries().then(r => r);
    }, []);

    // const getStateColor = (state) => {
    //     let color;
    //     switch (state) {
    //         case 'waiting':
    //             color = "#ff9800";
    //             // sarı
    //             break;
    //         case 'success':
    //             color = "#43AA41";
    //             // yeşil
    //             break;
    //         case 'error':
    //             color = "#D45D5E";
    //             // kırmızı
    //             break;
    //         case 'inprogress':
    //             color = "#2196f3";
    //             // mavi
    //             break;
    //         default:
    //             color = "#2196f3";
    //             break;
    //     }
    //     return color;
    // }


    const downloadLibraries = async (e) => {
        setInformation({ percentage: '40%', message: t(label + 'DOWNLOADING-AND-UPDATING') })
        let copyLibraries = [...libraries];
        /* all libraries are downloaded or updated one by one */
        for (let library of copyLibraries) {
            if (!library.downloaded) {
                // library.state = 'inprogress';
                // setLibraries(copyLibraries);
                await sleep(1000)
                const response = await LibraryService.downloadCustomLibraryByLabel({ library_label: library.label, version: library.version });
                if (response) {
                    if (response.status === true) {
                        library.downloaded = true;
                        library.library_exist = true;
                        library.existing_version = library.version;
                        library.is_active = true;
                        library.state = 'success';
                        //setLibraries([...copyLibraries]);
                    } else {
                        library.state = 'error';
                        library.error_message = t("MESSAGES.LIBRARY." + response.message);
                        setInformation(undefined);
                        setLibraries([...copyLibraries]);
                        setDownloadLibraryError(true);
                        return;
                    }
                } else {
                    library.state = 'error';
                    library.error_message = t('GENERAL.NETWORK-ERROR');
                    setInformation(undefined);
                    setLibraries([...copyLibraries]);
                    setDownloadLibraryError(true);
                    return;
                }
            }

        }
        setLibraries([...copyLibraries]);
        setDownloadLibraryError(false);
        if (callFunction && !downloadBot) {
            callFunction()
            handleClose();
        }
        await sleep(2000);
        setInformation({ percentage: '70%', message: t(label + 'DOWNLOADING-AUTOM') })
        await sleep(2000);
        if (downloadBot) {
            downloadBot((response) => {
                if (response.status) {
                    setInformation({ percentage: undefined, icon: successIcon, message: t(label + 'DOWNLOADED-SUCCESSFUL') })
                }
                setTimeout(() => {
                    handleClose();
                    setInformation(undefined)
                }, 5000)
            });
        }
    }

    return (
        information ?
            <StepBar percentage={information.percentage} message={information.message} icon={information.icon} />
            :
            <Dialog open={true} maxWidth="md" >
                <div className='required-library-container'>
                    <div className="bot-dialog-close"><Close fontSize="small" onClick={() => {
                        if (handleCloseOutPanel) {
                            handleCloseOutPanel()
                        }
                        handleClose(false)
                    }} /></div>
                    <div className='required-library-area'>
                        <LazyLoad className='warning-icon-container' height={33} >
                            <img src={warningIcon} height={33} />
                        </LazyLoad>
                        <div className='required-action t-a-c'>{downloadLibraryError ? t(label + "SOMETHING-WENT-WRONG") : t(label + "REQUIRED-ACTION")}</div>
                        <div className='required-library-desc t-a-c'>
                            {downloadLibraryError ? t(label + "ERROR-DESC") :
                                libraries.length + t(label + "REQUIRED-LIBRARY-DESC")}
                        </div>
                        <div className='table-th-container'>
                            <div className='title-text'>{t(label + "LIBRARY-NAME")}</div>
                            <div className='title-text'>{t(label + "CURRENT-VER")}</div>
                            <div className='title-text'>{t(label + "REQUIRED-VER")}</div>
                            <div className='title-text'>{t(label + "REMARKS")}</div>
                        </div>
                        {
                            libraries && libraries.map((lib, index) => {
                                if (!downloadLibraryError || lib.error_message) {
                                    return (
                                        <>
                                            <div className={`table-td-container ${lib.error_message ? 'error' : ''} ${collapse ? 'collapse' : ''}`} key={index}>
                                                <div className='required-library-name parent-col-container required-version-container'>
                                                    {lib.error_message &&
                                                        <LazyLoad className='arrow-icon-container' >
                                                            <img className='arrow-icon' src={collapse ? downArrowIcon : upArrowIcon} onClick={() => setCollapse(!collapse)} />
                                                        </LazyLoad>
                                                    }
                                                    {lib.label.includes('CUSTOM') ? lib.name :
                                                        t("CONTENT.LIBRARY." + lib.name + ".NAME")}
                                                </div>
                                                <div className={`current-version parent-col-container ${lib.existing_version ? 'active' : 'pasif'}`}>
                                                    {lib.existing_version ? `V${lib.existing_version}` : 'NA'}
                                                </div>
                                                <div className='td-text parent-col-container required-version-container'>
                                                    <span className='required-version-text'>{`V${lib.version}`}</span>
                                                </div>

                                                <div className='td-text remark-text'>
                                                    {lib.error_message ? t(label + "ERROR") : (lib.library_exist ? t(label + "NEEDS-UPDATE") : t(label + "DOWNLOAD-REQ"))}
                                                </div>
                                            </div>
                                            {
                                                lib.error_message && collapse &&
                                                <div className='error-area'>
                                                    <div className='remark-error'>{t(label + "ERROR")}</div>
                                                    <div className='error-message'>{lib.error_message}</div>
                                                </div>
                                            }
                                        </>
                                    )
                                }
                            })
                        }
                        <div className='confirm-button-container'>
                            <Button className='download-library-button' onClick={downloadLibraries}>{downloadLibraryError ? t(label + "RETRY") : t(label + "UPDATE-AND-DOWNLOAD")}</Button>
                        </div>
                    </div >
                </div>
            </Dialog>
    );
}

export default RequiredLibraries;
